import type { AIProviderConfig } from '@recipes/contracts';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  Input,
  Label,
  Spinner,
  Switch,
} from '@recipes/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AdminPage } from '@/features/admin/admin-layout';
import { FormField } from '@/features/shared/form-field';
import { api } from '@/lib/api';
import { describeError, toastError } from '@/lib/errors';
import { queryKeys } from '@/lib/query-keys';
import { toast } from '@/components/feedback/toaster';

export function AdminAiProvidersPage() {
  const { t } = useTranslation();
  const providers = useQuery({
    queryKey: queryKeys.admin.aiProviders,
    queryFn: () => api.admin.aiProviders(),
  });
  return (
    <AdminPage section="ai" description={t('admin.ai.description')}>
      {providers.isLoading && <Spinner label={t('app.loading')} />}
      {providers.error && <Alert variant="destructive">{describeError(providers.error)}</Alert>}
      <div className="grid gap-4 lg:grid-cols-2">
        {(providers.data ?? []).map((p) => (
          <ProviderCard key={p.provider} config={p} />
        ))}
      </div>
    </AdminPage>
  );
}

function ProviderCard({ config }: { config: AIProviderConfig }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    enabled: config.enabled,
    defaultModel: config.defaultModel,
    visionModel: config.visionModel,
    priority: String(config.priority),
    maxRequestsPerUserPerDay: String(config.limits.maxRequestsPerUserPerDay),
    maxOutputTokens: String(config.limits.maxOutputTokens),
    maxCostUsdPerUserPerDay:
      config.limits.maxCostUsdPerUserPerDay == null
        ? ''
        : String(config.limits.maxCostUsdPerUserPerDay),
    region: config.region ?? '',
  });
  const set = (key: keyof typeof form, value: string | boolean) =>
    setForm((f) => ({ ...f, [key]: value }));
  const save = useMutation({
    mutationFn: () =>
      api.admin.updateAiProvider(config.provider, {
        enabled: form.enabled,
        defaultModel: form.defaultModel.trim(),
        visionModel: form.visionModel.trim(),
        priority: Number(form.priority) || 0,
        limits: {
          maxRequestsPerUserPerDay: Number(form.maxRequestsPerUserPerDay) || 0,
          maxOutputTokens: Number(form.maxOutputTokens) || 0,
          maxCostUsdPerUserPerDay:
            form.maxCostUsdPerUserPerDay.trim() === ''
              ? null
              : Number(form.maxCostUsdPerUserPerDay),
        },
        region: form.region.trim() || null,
      }),
    onSuccess: () => {
      toast.success(t('admin.ai.saved', { provider: config.provider }));
      void queryClient.invalidateQueries({ queryKey: queryKeys.admin.aiProviders });
    },
    onError: (e) => toastError(e),
  });
  const id = `provider-${config.provider}`;

  return (
    <Card>
      <CardContent className="p-4 md:p-6">
        <form
          noValidate
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-lg font-semibold">{config.provider}</h2>
            <div className="flex items-center gap-2">
              <Badge variant={config.hasApiKey ? 'success' : 'destructive'}>
                {config.hasApiKey ? t('admin.ai.hasKey') : t('admin.ai.noKey')}
              </Badge>
              <Label htmlFor={`${id}-enabled`} className="text-sm">
                {t('admin.ai.enabled')}
              </Label>
              <Switch
                id={`${id}-enabled`}
                checked={form.enabled}
                onCheckedChange={(v) => set('enabled', v)}
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">{t('admin.ai.keyHint')}</p>
          <div className="grid grid-cols-2 gap-3">
            <FormField label={t('admin.ai.defaultModel')}>
              <Input
                value={form.defaultModel}
                onChange={(e) => set('defaultModel', e.target.value)}
              />
            </FormField>
            <FormField label={t('admin.ai.visionModel')}>
              <Input
                value={form.visionModel}
                onChange={(e) => set('visionModel', e.target.value)}
              />
            </FormField>
            <FormField label={t('admin.ai.priority')} hint={t('admin.ai.priorityHint')}>
              <Input
                type="number"
                inputMode="numeric"
                value={form.priority}
                onChange={(e) => set('priority', e.target.value)}
              />
            </FormField>
            <FormField label={t('admin.ai.region')} optionalText={t('common.optional')}>
              <Input value={form.region} onChange={(e) => set('region', e.target.value)} />
            </FormField>
            <FormField label={t('admin.ai.maxRequests')}>
              <Input
                type="number"
                min={0}
                inputMode="numeric"
                value={form.maxRequestsPerUserPerDay}
                onChange={(e) => set('maxRequestsPerUserPerDay', e.target.value)}
              />
            </FormField>
            <FormField label={t('admin.ai.maxTokens')}>
              <Input
                type="number"
                min={0}
                inputMode="numeric"
                value={form.maxOutputTokens}
                onChange={(e) => set('maxOutputTokens', e.target.value)}
              />
            </FormField>
            <FormField label={t('admin.ai.maxCost')} optionalText={t('common.optional')}>
              <Input
                type="number"
                min={0}
                step="0.01"
                inputMode="decimal"
                value={form.maxCostUsdPerUserPerDay}
                onChange={(e) => set('maxCostUsdPerUserPerDay', e.target.value)}
              />
            </FormField>
          </div>
          <Button type="submit" loading={save.isPending}>
            {t('common.save')}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
